// Reduced "transaction shell" header for mid-flow pages (Seller/Buyer journeys),
// adapted from Klargo-Reference/components/layout/AppHeader.tsx's isTransactionShell
// branch: dark ink bar, logo only (no primary nav, no account menu), language
// switcher, and a Help control that opens a side sheet (same slide-in-from-right
// canvas mechanics as Klargo-Reference's HelpSheet.tsx).
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { guardedNavigate } from './DealSessionManager';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import DsLogo from './DsLogo';
import DealExpiryCountdown from './DealExpiryCountdown';
import DropdownProfile from '../../../components/header/dropdown/profile';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { useLanguage } from '../layout/LanguageSwitcher';
import { ForwardArrow } from './StatusMark';
import './TransactionHeader.css';

const TransactionHeader = ({ homePath = '/user/ChooseAction' }) => {
  const { t } = useTranslation();
  const { t: tp, languageCode } = useLanguage();
  const [helpOpen, setHelpOpen] = useState(false);

  // Help links leave the deal flow, so they go through the leave-deal confirmation.
  const goTo = (path) => {
    setHelpOpen(false);
    guardedNavigate(path);
  };

  return (
    <header
      className="sticky top-0 z-50 bg-ds-ink text-ds-ink-on-soft"
      style={{ boxShadow: '0 1px 0 rgba(0,0,0,.25)' }}
    >
      <div className="mx-auto flex h-14 max-w-[1200px] items-center justify-between px-5 md:px-8">
        <Link to={homePath} aria-label="Klargo" className="flex shrink-0 items-center gap-2 px-0.5 py-3 no-underline">
          <DsLogo />
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <DealExpiryCountdown />
          <DropdownProfile focused hideAccount />
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            data-testid="shell-help"
            className="ds-help-btn inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium text-ds-ink-on"
          >
            <HelpCircle size={14} aria-hidden="true" />
            <span className="hidden sm:inline">{t('profile.menu.help')}</span>
          </button>
        </div>
      </div>

      <Sheet open={helpOpen} onOpenChange={setHelpOpen}>
        <SheetContent
          side="right"
          className="ds-scope flex h-[100dvh] w-full flex-col border-ds-rule-edge bg-ds-sheet font-ds-sans text-ds-body sm:max-w-md"
        >
          <SheetHeader className="shrink-0 border-b border-ds-rule-row pb-4">
            <SheetTitle className="ds-title-chapter text-left">{tp('help.title')}</SheetTitle>
          </SheetHeader>

          <div className="flex-1 space-y-6 overflow-y-auto overscroll-contain py-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            <p className="ds-body m-0">{tp('help.sheet.introTransaction')}</p>

            <div className="flex flex-col items-start gap-1">
              <button type="button" onClick={() => goTo('/support')} className="ds-action-text">
                {tp('ds.support.title')}
                <ForwardArrow stroke="currentColor" width={14} height={11} />
              </button>
              <p className="ds-meta m-0">{tp('help.sheet.supportDescription')}</p>
            </div>

            <button
              type="button"
              onClick={() => goTo(languageCode === 'en' ? '/en/security-and-fraud' : '/sakerhet-och-bedragerier')}
              className="ds-action-text"
            >
              {tp('help.security')}
              <ForwardArrow stroke="currentColor" width={14} height={11} />
            </button>

            <div className="border-t border-ds-rule-row pt-6">
              <h3 className="ds-title-component m-0 mb-2">{tp('footer.contact.title')}</h3>
              <p className="m-0 text-[15px] font-semibold leading-[1.35] text-ds-ink">support@klargo.se</p>
              <p className="ds-body m-0 mt-1">{tp('ds.support.contactHours')}</p>
              <a href="mailto:support@klargo.se" className="ds-action-text">
                {tp('help.contact.cta')}
                <ForwardArrow stroke="currentColor" width={14} height={11} />
              </a>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default TransactionHeader;
